import type { IconName } from '@customerio/pluma-icons';

/**
 * The message channels the message row can pick, a scoped port of the
 * segmentable slice of the Ember action catalog (`app/utils/action-info.ts`):
 * per channel a menu label, the short name the picker's wildcard copy uses
 * ("any push"), and an icon. Email is not an action but always leads the
 * menu. Declared in the catalog's `MESSAGING_TYPES` order, which is the order
 * the menu renders in.
 */
export type MessageChannel = {
  value: string;
  label: string;
  /** Lower-case-ish short name for wildcard copy ("any {shortName}"). */
  shortName: string;
  icon: IconName;
};

export const EMAIL_CHANNEL: MessageChannel = { value: 'email', label: 'Email', shortName: 'email', icon: 'email' };

export const SEGMENTABLE_ACTION_CHANNELS: MessageChannel[] = [
  { value: 'push', label: 'Push Notification', shortName: 'push', icon: 'mobile' },
  { value: 'slack', label: 'Slack Message', shortName: 'Slack message', icon: 'slack' },
  { value: 'twilio', label: 'SMS', shortName: 'SMS', icon: 'sms' },
  { value: 'sms', label: 'SMS', shortName: 'SMS', icon: 'sms' },
  { value: 'urban_airship', label: 'Airship', shortName: 'UA push', icon: 'mobile' },
  { value: 'webhook', label: 'Webhook', shortName: 'webhook', icon: 'webhook' },
  { value: 'in_app', label: 'In-App Message', shortName: 'in-app', icon: 'in-app' },
  { value: 'inbox', label: 'Inbox Message', shortName: 'Inbox Message', icon: 'notifications' },
  { value: 'line', label: 'LINE Message', shortName: 'LINE', icon: 'brand/line' },
  { value: 'whatsapp', label: 'WhatsApp', shortName: 'WhatsApp', icon: 'brand/whatsapp' },
];

const CHANNELS_BY_VALUE = new Map<string, MessageChannel>(
  [EMAIL_CHANNEL, ...SEGMENTABLE_ACTION_CHANNELS].map((channel) => [channel.value, channel]),
);

/** The catalog entry for a channel value, or null for a non-channel string. */
export function messageChannelOf(value: string): MessageChannel | null {
  return CHANNELS_BY_VALUE.get(value) ?? null;
}

/**
 * The picker value for "any message on this channel": email spells it as the
 * bare `*`, every action as `<channel>_*`.
 */
export function anyMessageValue(channel: string): string {
  return channel === 'email' ? '*' : `${channel}_*`;
}

export type SmsChannel = 'sms' | 'twilio';

/**
 * Legacy Twilio ("twilio") and custom SMS ("sms") both surface as "SMS", so
 * only one is offered wherever both could appear. Custom SMS is the default;
 * the `legacy-twilio` flag pins a workspace back to legacy Twilio, as does an
 * already-provisioned Twilio integration (read from the raw `enabled_actions`
 * list, NOT the enabled-action check, which treats twilio as always on).
 */
export function preferredSmsChannel({
  legacyTwilioEnabled,
  twilioEnabled,
}: {
  legacyTwilioEnabled: boolean;
  twilioEnabled: boolean;
}): SmsChannel {
  if (legacyTwilioEnabled || twilioEnabled) {
    return 'twilio';
  }
  return 'sms';
}
